import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Plus, Edit3, Trash2, Save, Brain, BookOpen, Target, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { levels, stories } from '../data/mock';

const GrammarAdmin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State for grammar topics
  const [grammarTopics, setGrammarTopics] = useState({
    'A1': [
      { 
        id: 1, 
        name: 'Artikel (der, die, das)', 
        explanation: 'Deutsche Artikel bestimmen das Geschlecht von Substantiven. "Der" ist maskulin, "die" ist feminin, "das" ist neutral.',
        examples: ['der Mann', 'die Frau', 'das Kind'],
        level: 'A1'
      },
      { 
        id: 2, 
        name: 'Präsens', 
        explanation: 'Das Präsens drückt gegenwärtige Handlungen oder allgemeingültige Aussagen aus.',
        examples: ['Ich gehe zur Schule.', 'Sie arbeitet jeden Tag.', 'Wir lernen Deutsch.'],
        level: 'A1'
      }
    ],
    'A2': [
      { 
        id: 3, 
        name: 'Perfekt', 
        explanation: 'Das Perfekt wird mit haben/sein + Partizip II gebildet und drückt abgeschlossene Handlungen aus.',
        examples: ['Ich habe gegessen.', 'Er ist gegangen.', 'Wir haben gelernt.'],
        level: 'A2'
      }
    ],
    'B1': [
      { 
        id: 4, 
        name: 'Relativsätze', 
        explanation: 'Relativsätze geben zusätzliche Informationen über ein Substantiv und werden mit Relativpronomen eingeleitet.',
        examples: ['Der Mann, der dort steht, ist mein Vater.', 'Das Buch, das ich lese, ist interessant.'],
        level: 'B1'
      }
    ],
    'B2': [], 'C1': []
  });
  
  // State for sentence-grammar assignments
  const [sentenceAssignments, setSentenceAssignments] = useState({
    1: [ // Story ID 1
      {
        id: 1,
        text: 'Hast du schon einmal an eine Scheune gedacht',
        storyId: 1,
        grammarTopics: [2], // Perfekt
        position: { start: 0, end: 45 }
      },
      {
        id: 2,
        text: 'eine Party-Scheune zu haben',
        storyId: 1,
        grammarTopics: [1], // Infinitiv mit zu
        position: { start: 120, end: 145 }
      }
    ]
  });

  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [selectedStory, setSelectedStory] = useState(1);
  const [isEditingTopic, setIsEditingTopic] = useState(false);
  const [currentTopic, setCurrentTopic] = useState({
    id: null,
    name: '',
    explanation: '',
    examples: [''],
    level: 'A1'
  });
  
  // Sentence assignment state
  const [isAssigningSentence, setIsAssigningSentence] = useState(false);
  const [sentenceForm, setSentenceForm] = useState({
    text: '',
    grammarTopics: [],
    storyId: 1
  });

  const handleCreateTopic = () => {
    setCurrentTopic({
      id: null,
      name: '',
      explanation: '',
      examples: [''],
      level: selectedLevel
    });
    setIsEditingTopic(true);
  };

  const handleEditTopic = (topic) => {
    setCurrentTopic({...topic});
    setIsEditingTopic(true);
  };

  const handleSaveTopic = () => {
    const newTopic = {
      ...currentTopic,
      id: currentTopic.id || Date.now(),
      examples: currentTopic.examples.filter(ex => ex.trim() !== '')
    };

    setGrammarTopics(prev => {
      const levelTopics = prev[selectedLevel] || [];
      if (currentTopic.id) {
        // Update existing
        return {
          ...prev,
          [selectedLevel]: levelTopics.map(topic => 
            topic.id === currentTopic.id ? newTopic : topic
          )
        };
      } else {
        // Create new
        return {
          ...prev,
          [selectedLevel]: [...levelTopics, newTopic]
        };
      }
    });

    toast({
      title: "Grammatikthema gespeichert",
      description: `"${newTopic.name}" wurde erfolgreich gespeichert.`,
    });

    setIsEditingTopic(false);
  };

  const handleDeleteTopic = (topicId) => {
    setGrammarTopics(prev => ({
      ...prev,
      [selectedLevel]: prev[selectedLevel].filter(topic => topic.id !== topicId)
    }));

    toast({
      title: "Grammatikthema gelöscht",
      description: "Das Grammatikthema wurde entfernt.",
      variant: "destructive",
    });
  };

  const handleAddExample = () => {
    setCurrentTopic(prev => ({
      ...prev,
      examples: [...prev.examples, '']
    }));
  };

  const handleUpdateExample = (index, value) => {
    setCurrentTopic(prev => ({
      ...prev,
      examples: prev.examples.map((ex, i) => i === index ? value : ex)
    }));
  };

  const handleRemoveExample = (index) => {
    setCurrentTopic(prev => ({
      ...prev,
      examples: prev.examples.filter((_, i) => i !== index)
    }));
  };

  const handleCreateSentenceAssignment = () => {
    setSentenceForm({
      text: '',
      grammarTopics: [],
      storyId: selectedStory
    });
    setIsAssigningSentence(true);
  };

  const handleSaveSentenceAssignment = () => {
    const newAssignment = {
      id: Date.now(),
      ...sentenceForm,
      position: { start: 0, end: sentenceForm.text.length }
    };

    setSentenceAssignments(prev => ({
      ...prev,
      [selectedStory]: [...(prev[selectedStory] || []), newAssignment]
    }));

    toast({
      title: "Satz-Zuordnung gespeichert",
      description: "Die Grammatikthemen wurden dem Satz zugeordnet.",
    });

    setIsAssigningSentence(false);
  };

  const handleDeleteSentenceAssignment = (assignmentId) => {
    setSentenceAssignments(prev => ({
      ...prev,
      [selectedStory]: prev[selectedStory].filter(assignment => assignment.id !== assignmentId)
    }));

    toast({
      title: "Zuordnung gelöscht",
      description: "Die Satz-Grammatik-Zuordnung wurde entfernt.",
      variant: "destructive",
    });
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getAllGrammarTopics = () => {
    return Object.values(grammarTopics).flat();
  };

  const getTopicById = (id) => {
    return getAllGrammarTopics().find(topic => topic.id === id);
  };

  const currentStory = stories.find(s => s.id === selectedStory);
  const currentAssignments = sentenceAssignments[selectedStory] || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Brain className="h-6 w-6 text-[#4ECDC4]" />
                <h1 className="text-xl font-bold">Grammatik-Verwaltung</h1>
              </div>
            </div>
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
              onClick={() => navigate(`/story/${selectedStory}`)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Story ansehen
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="topics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="topics" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Grammatikthemen verwalten
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Sätze zuordnen
            </TabsTrigger>
          </TabsList>

          {/* Grammar Topics Management */}
          <TabsContent value="topics" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {levels.map((level) => (
                      <SelectItem key={level.code} value={level.code}>
                        {level.code} - {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={handleCreateTopic}
              >
                <Plus className="h-4 w-4 mr-2" />
                Neues Grammatikthema
              </Button>
            </div>

            {isEditingTopic ? (
              /* Edit Form */
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>
                    {currentTopic.id ? 'Grammatikthema bearbeiten' : 'Neues Grammatikthema erstellen'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Themenname</label>
                      <Input
                        value={currentTopic.name}
                        onChange={(e) => setCurrentTopic({...currentTopic, name: e.target.value})}
                        placeholder="z.B. Artikel (der, die, das)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Niveau</label>
                      <Select 
                        value={currentTopic.level} 
                        onValueChange={(value) => setCurrentTopic({...currentTopic, level: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {levels.map((level) => (
                            <SelectItem key={level.code} value={level.code}>
                              {level.code} - {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Erläuterung</label>
                    <Textarea
                      value={currentTopic.explanation}
                      onChange={(e) => setCurrentTopic({...currentTopic, explanation: e.target.value})}
                      placeholder="Detaillierte Erklärung des Grammatikthemas..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium">Beispiele</label>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleAddExample}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Beispiel hinzufügen
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {currentTopic.examples.map((example, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={example}
                            onChange={(e) => handleUpdateExample(index, e.target.value)}
                            placeholder={`Beispiel ${index + 1}`}
                          />
                          {currentTopic.examples.length > 1 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveExample(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                      onClick={handleSaveTopic}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Speichern
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setIsEditingTopic(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Topics List */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(grammarTopics[selectedLevel] || []).map((topic) => (
                  <Card key={topic.id} className="border-0 shadow-lg">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{topic.name}</CardTitle>
                          <Badge className={getLevelColor(topic.level)} size="sm">
                            {topic.level}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTopic(topic)}
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTopic(topic.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-700 mb-3">
                        {topic.explanation}
                      </p>
                      {topic.examples.length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-600 mb-2">Beispiele:</h4>
                          <div className="space-y-1">
                            {topic.examples.slice(0, 2).map((example, i) => (
                              <div key={i} className="text-xs bg-blue-50 p-2 rounded">
                                {example}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sentence Assignments */}
          <TabsContent value="assignments" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Select value={selectedStory.toString()} onValueChange={(value) => setSelectedStory(parseInt(value))}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stories.map((story) => (
                      <SelectItem key={story.id} value={story.id.toString()}>
                        {story.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={handleCreateSentenceAssignment}
              >
                <Plus className="h-4 w-4 mr-2" />
                Satz zuordnen
              </Button>
            </div>

            {/* Story Preview */}
            {currentStory && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-[#4ECDC4]" />
                    {currentStory.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-800 leading-relaxed">
                      {currentStory.content.split('\n\n')[0]}...
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isAssigningSentence ? (
              /* Assignment Form */
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Satz mit Grammatikthemen verknüpfen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Satz oder Textabschnitt</label>
                    <Textarea
                      value={sentenceForm.text}
                      onChange={(e) => setSentenceForm({...sentenceForm, text: e.target.value})}
                      placeholder="Geben Sie den Satz ein, der mit Grammatikthemen verknüpft werden soll..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Grammatikthemen auswählen</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {getAllGrammarTopics().map((topic) => (
                        <Badge
                          key={topic.id}
                          variant={sentenceForm.grammarTopics.includes(topic.id) ? "default" : "outline"}
                          className={`cursor-pointer ${
                            sentenceForm.grammarTopics.includes(topic.id) 
                              ? "bg-[#4ECDC4] text-white" 
                              : "hover:bg-slate-100"
                          }`}
                          onClick={() => {
                            const isSelected = sentenceForm.grammarTopics.includes(topic.id);
                            setSentenceForm(prev => ({
                              ...prev,
                              grammarTopics: isSelected 
                                ? prev.grammarTopics.filter(id => id !== topic.id)
                                : [...prev.grammarTopics, topic.id]
                            }));
                          }}
                        >
                          {topic.name} ({topic.level})
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                      onClick={handleSaveSentenceAssignment}
                      disabled={!sentenceForm.text.trim() || sentenceForm.grammarTopics.length === 0}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Zuordnung speichern
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setIsAssigningSentence(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Assignments List */
              <div className="space-y-4">
                {currentAssignments.map((assignment) => (
                  <Card key={assignment.id} className="border-0 shadow-lg">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="bg-[#4ECDC4] bg-opacity-10 p-3 rounded-lg mb-3">
                            <p className="font-medium text-[#4ECDC4]">
                              "{assignment.text}"
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {assignment.grammarTopics.map((topicId) => {
                              const topic = getTopicById(topicId);
                              return topic ? (
                                <Badge key={topicId} className={getLevelColor(topic.level)}>
                                  {topic.name}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteSentenceAssignment(assignment.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {currentAssignments.length === 0 && (
                  <Alert>
                    <AlertDescription>
                      Noch keine Satz-Zuordnungen für diese Geschichte. Klicken Sie auf "Satz zuordnen", um zu beginnen.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GrammarAdmin;